import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { RolesPermisosService } from '../../role-management/services/roles-permisos.service';
import { jwtDecode } from 'jwt-decode';
import { of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PermisosService {
  // 🟢 Signal reactivo con permisos actuales
  permisos = signal(this.permisosFalse());

  constructor(
    private readonly rolesPermisosService: RolesPermisosService,
    private readonly router: Router
  ) { }

  private obtenerRolId(): number {
    const token = localStorage.getItem('token');
    if (!token) return 0;

    try {
      const decoded: any = jwtDecode(token);
      return Number(decoded?.rolId || decoded?.idRol || 0);
    } catch {
      console.error('⚠️ Error al decodificar token');
      return 0;
    }
  }

  private permisosFalse() {
    return { ver: false, agregar: false, editar: false, eliminar: false };
  }

  private permisosAdmin() {
    return { ver: true, agregar: true, editar: true, eliminar: true };
  }

  private toBool(v: any): boolean {
    return v === 1 || v === true;
  }

  private getBasePath(): string {
    const url = this.router.url.toLowerCase().replace(/^\/+/, '').split('?')[0];
    const segments = url.split('/');

    // Filtramos segmentos que sean IDs (números o UUIDs simples)
    const cleanSegments = segments.filter(seg => isNaN(Number(seg)) && seg.length < 20);

    // Tomamos hasta 2 segmentos base (ej: 'admin/users' o 'nino-shopping/registrarcompra')
    return cleanSegments.slice(0, 2).join('/');
  }

  private getRutaId(basePath: string): number | null {
    const rutaMap = JSON.parse(localStorage.getItem('rutaMap') || '{}');

    // 1. Intento exacto (ej: 'admin/users')
    let found = Object.keys(rutaMap).find(
      id => rutaMap[Number(id)].toLowerCase() === basePath
    );

    // 2. Si no encuentra, intentamos con el último segmento (ej: 'users')
    if (!found) {
      const lastSegment = basePath.split('/').pop();
      if (lastSegment) {
        found = Object.keys(rutaMap).find(
          id => rutaMap[Number(id)].toLowerCase().endsWith(lastSegment)
        );
      }
    }

    return found ? Number(found) : null;
  }

  private extraerPermisosLista(data: any[], idRuta: number | null, rolId: number, basePath?: string) {
    if (rolId === 1) {
      return this.permisosAdmin();
    }

    if (!Array.isArray(data) || data.length === 0) {
      console.warn('⚠️ Sin datos de permisos para rol', rolId);
      return this.permisosFalse();
    }

    let permiso = idRuta ? data.find(p => Number(p.idRuta) === idRuta) : null;

    if (!permiso && basePath) {
      const cleanBase = basePath.toLowerCase().replace(/^\/+/, '');
      const lastSegment = cleanBase.split('/').pop();
      permiso = data.find(p => {
        const r = (p.ruta || '').toLowerCase().replace(/^\/+/, '');
        return r === cleanBase || (lastSegment && r.endsWith(lastSegment));
      });
    }

    if (!permiso) {
      console.warn('🚫 No se encontraron permisos para ruta:', idRuta || basePath);
      return this.permisosFalse();
    }

    return {
      ver: this.toBool(permiso.acceso),
      agregar: this.toBool(permiso.agregar),
      editar: this.toBool(permiso.editar),
      eliminar: this.toBool(permiso.eliminar),
    };
  }

  cargarPermisosAuto(rutaManual?: string) {
    const rolId = this.obtenerRolId();
    if (!rolId) {
      console.warn('⚠️ No hay rolId');
      this.permisos.set(this.permisosFalse());
      return of(this.permisosFalse());
    }

    // ⭐ Si es Administrador (rolId = 1), siempre otorgar todos los permisos
    if (rolId === 1) {
      const fullPerms = this.permisosAdmin();
      this.permisos.set(fullPerms);
      return of(fullPerms);
    }

    const basePath = rutaManual ? rutaManual.toLowerCase().replace(/^\/+/, '') : this.getBasePath();
    let idRuta = this.getRutaId(basePath);

    return this.rolesPermisosService.getPermissionsById(rolId).pipe(
      map(data => {
        // Aprovechar la llamada para sincronizar rutaMap si está vacía
        if (Array.isArray(data) && data.length > 0) {
          try {
            const mapObj = JSON.parse(localStorage.getItem('rutaMap') || '{}');
            let updated = false;
            data.forEach((p: any) => {
              if (p.idRuta && p.ruta && !mapObj[p.idRuta]) {
                mapObj[p.idRuta] = p.ruta.toLowerCase();
                updated = true;
              }
            });
            if (updated) {
              localStorage.setItem('rutaMap', JSON.stringify(mapObj));
            }
          } catch {}
        }
        return this.extraerPermisosLista(data, idRuta, rolId, basePath);
      }),
      tap(permisos => {
        this.permisos.set(permisos);
      }),
      catchError(err => {
        console.error('❌ Error al cargar permisos:', err);
        this.permisos.set(this.permisosFalse());
        return of(this.permisosFalse());
      })
    );
  }

  getPermisos() {
    return this.permisos();
  }

  limpiarCache() {
    this.permisos.set(this.permisosFalse());
  }
}
